import { Controller } from '@hotwired/stimulus';
import log from '../lib/logger';
import { loadJQueryDataTables } from '../lib/datatables/load_jquery_datatables';

export default class extends Controller {
  static targets = ['table'];
  static values = {
    tableId: String,
    orderColumn: { type: Number, default: 0 },
    orderDirection: { type: String, default: 'desc' },
    options: { type: Object, default: {} },
  };

  connect() {
    log.info(`DataTable AJAX controller connected for table: ${this.tableIdValue}`);

    // Set up observer for ajax-partial content
    this.setupAjaxPartialListener();

    // Also check if table already exists (in case partial loaded before controller)
    this.checkAndInitializeTable();
  }

  disconnect() {
    // Clean up DataTable if it exists
    if (this.dataTable) {
      if (window.$.fn.dataTable.isDataTable(this.dataTable.table().node())) this.dataTable.destroy();
      this.dataTable = null;
    }

    // Remove any observers
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupAjaxPartialListener() {
    // Find the closest ajax-partial element
    const ajaxPartial = this.element.querySelector('ajax-partial');

    if (!ajaxPartial) {
      log.warn('No ajax-partial element found');
      return;
    }

    // Listen for various possible ajax-partial events
    const eventNames = ['ajax-partial:loaded', 'ajax-partial:loadend', 'ajax-partial:complete', 'load'];

    eventNames.forEach((eventName) => {
      ajaxPartial.addEventListener(eventName, () => {
        log.info(`Ajax partial event fired: ${eventName}`);
        this.initializeDataTable();
      });
    });

    // Also set up a MutationObserver as backup
    this.observer = new MutationObserver(() => {
      // Check if the table was added
      if (document.getElementById(this.tableIdValue)) {
        this.initializeDataTable();
      }
    });

    this.observer.observe(ajaxPartial, {
      childList: true,
      subtree: true,
    });
  }

  checkAndInitializeTable() {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      const table = document.getElementById(this.tableIdValue);
      if (table) {
        this.initializeDataTable();
      }
    });
  }

  async initializeDataTable() {
    // Prevent double initialization
    if (this.dataTable || this.initializing) {
      return;
    }

    const table = document.getElementById(this.tableIdValue);
    if (!table) {
      log.info(`Table ${this.tableIdValue} not found yet`);
      return;
    }

    this.initializing = true;

    try {
      await loadJQueryDataTables();
      if (!this.element.isConnected || !table.isConnected) return;

      // Check if already initialized
      if (window.$.fn.dataTable.isDataTable(`#${this.tableIdValue}`)) {
        log.info(`DataTable already initialized for ${this.tableIdValue}`);
        this.dataTable = window.$(`#${this.tableIdValue}`).DataTable();
        return;
      }

      // Build options object
      const options = {
        order: [[this.orderColumnValue, this.orderDirectionValue]],
        ...this.optionsValue,
      };

      log.info(`Initializing DataTable for ${this.tableIdValue} with options:`, options);

      // Initialize DataTable
      this.dataTable = window.$(`#${this.tableIdValue}`).DataTable(options);

      log.info(`DataTable initialized successfully for ${this.tableIdValue}`);

      // Disconnect observer once initialized
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    } catch (error) {
      log.error(`Error initializing DataTable for ${this.tableIdValue}:`, error);
    } finally {
      this.initializing = false;
    }
  }

  // Public method to manually reinitialize
  refresh() {
    if (this.dataTable) {
      this.dataTable.destroy();
      this.dataTable = null;
    }
    this.initializeDataTable();
  }

  // Public method to get the DataTable instance
  getDataTable() {
    return this.dataTable;
  }
}
